import { filterOrganizationsForUser } from '../../../../backend/features/organizations/shared';
import { useUserState } from '../../../../ui/hooks';
import { useContactManagementTranslation } from '../../../../ui/hooks/useContactManagementTranslation';
import { useDataTreeUsingUrl, } from '@juvare/common-data-table-ui/DataTree';
import { usePlatformFunction } from '@juvare/platform-sdk-react/ui/hooks';
import cloneDeep from 'lodash.clonedeep';
import React from 'react';
import { filterOrganizationTreeItemsByFieldFilter } from '../components/OrganizationsPanel/utils/filterOrganizationTreeItemsByFieldFilter';
import { makeUniqueIds } from '../components/OrganizationsPanel/utils/makeUniqueIds';
import { orgHasMembersInChildren } from '../components/OrganizationsPanel/utils/orgHasMembersInChildren';
export function useOrganizationsTree({ useDataTreeFunction = useDataTreeUsingUrl, staticFilters, includeVisible, includeByMembership, includeByOrgPermission, }) {
    var _a;
    const { t } = useContactManagementTranslation();
    const userState = useUserState();
    const [totalPendingMemberships, setTotalPendingMemberships] = React.useState(0);
    const [orgMembershipCountMap, setOrgMembershipCountMap] = React.useState(new Map());
    const params = {
        useDataProvider: (request) => {
            const filters = Object.assign(Object.assign({}, staticFilters), request.filterData);
            const { search, permission, pending } = filters;
            const organizations = userState.organizations;
            const memberCountsResponse = usePlatformFunction('getOrgMemberCounts');
            const data = React.useMemo(() => {
                var _a;
                let pendingMemberships = 0;
                const organizationMembershipCountMap = new Map();
                // Need to make sure that there are no duplicate id's in any row, otherwise DataTree can get confused
                const orgs = cloneDeep(organizations);
                makeUniqueIds(orgs);
                // Load pending memberships
                if (!memberCountsResponse.isLoading && !memberCountsResponse.error && memberCountsResponse.data) {
                    memberCountsResponse.data.result.forEach((count) => {
                        pendingMemberships += count.memberCount;
                        organizationMembershipCountMap.set(count.organizationId, count.memberCount);
                    });
                    setTotalPendingMemberships(pendingMemberships);
                    setOrgMembershipCountMap(organizationMembershipCountMap);
                }
                // Apply any filters
                const filterResult = filterOrganizationTreeItemsByFieldFilter((_a = filterOrganizationsForUser(orgs, includeVisible !== null && includeVisible !== void 0 ? includeVisible : false, includeByMembership !== null && includeByMembership !== void 0 ? includeByMembership : false, includeByOrgPermission, userState)) !== null && _a !== void 0 ? _a : [], mapOrganizationFilterData({ search, permission, pending }), userState, organizationMembershipCountMap);
                filterResult.organizations = walkOrganizationTree(filterResult.organizations, organizationMembershipCountMap);
                return {
                    items: filterResult.organizations,
                    highlightedItemIds: filterResult.highlightedOrganizationIds.length > 0 ? filterResult.highlightedOrganizationIds : undefined,
                };
            }, [
                search,
                permission,
                pending,
                organizations,
                memberCountsResponse.data,
                memberCountsResponse.isLoading,
                memberCountsResponse.error,
            ]);
            return {
                data,
                error: memberCountsResponse.error,
                reload: memberCountsResponse.mutate,
                isLoading: memberCountsResponse.isLoading,
            };
        },
    };
    const { dataTreeProps, filtersBarProps, appliedFilterData, reload } = useDataTreeFunction(params);
    const defaultDataTreeProps = Object.assign(Object.assign({}, dataTreeProps), { title: t('panels.organizations.title'), 'data-test': 'organization-tree' });
    const selectedOrganizationIds = React.useMemo(() => {
        var _a;
        return (_a = dataTreeProps.selectedItemIds) === null || _a === void 0 ? void 0 : _a.map((id) => id.split('_')[0]);
    }, [dataTreeProps.selectedItemIds]);
    return {
        dataTreeProps: defaultDataTreeProps,
        filtersBarProps,
        appliedFilterData,
        reload,
        totalPendingMemberships,
        orgMembershipCountMap,
        activeOrganizationId: (_a = dataTreeProps.activeItemId) === null || _a === void 0 ? void 0 : _a.split('_')[0],
        selectedOrganizationIds,
    };
}
/**
 * maps OrganizationsFilterData (UI filters) to FieldFilter (used in actual organization filtering)
 * */
export function mapOrganizationFilterData(filterData) {
    const fieldFilters = [];
    if (filterData.search) {
        fieldFilters.push({ field: 'search', value: filterData.search });
    }
    if (filterData.permission) {
        fieldFilters.push({ field: 'permission', value: filterData.permission });
    }
    if (filterData.pending) {
        fieldFilters.push({ field: 'pending', value: filterData.pending });
    }
    return fieldFilters;
}
export function walkOrganizationTree(organizations, organizationMembershipCountMap) {
    return organizations.map((organization) => {
        let children = [];
        if (organization.children) {
            children = walkOrganizationTree(organization.children, organizationMembershipCountMap);
        }
        return Object.assign(Object.assign({}, organization), { children, childrenHasPendingMembers: orgHasMembersInChildren(organization.children, organizationMembershipCountMap), pendingMemberCount: organizationMembershipCountMap.get(organization.id) });
    });
}
